import { defaultHeight } from '@Common/Controls/SimpleGridLight/Editors/EditorProps';
import { TypeKind } from '@Common/Enums/Enum';
import { IGridColumnEditorProperties } from '../Editors/GridEditors';
import { IInputParameters, Input } from '../../index';
import localizationHelper from '@Common/Helpers/LocalizationHelper';
import { ICellEditor, IGridColumnEditor } from '../SimpleGridLight';
import { GridColumn } from '../GridColumn';
import { RowCollection } from '../RowCollection';
import { GridRow } from '../GridRow';
import { CellCheckbox } from '../Editors/CheckBoxEditor';
import { GridCell } from '../GridCell';
import { safeDestroys } from '@Common/Core/Destroyable';

export interface IGridInputEditorProperties extends IGridColumnEditorProperties, IInputParameters {
    onEditorChanged?: (cell: GridCell, editorValue: any) => void;
    getText?: (cell: GridCell) => string;
    onValidate?: (isValid: boolean) => void;
}

const gridInputEditorDefaultProperties: IGridInputEditorProperties = {
    type: TypeKind.string,
    getText(cell: GridCell) {
        const value = cell.value();
        if (value != null) {
            return value.toString();
        }
        return '';
    }
};

export class GridCellInputEditor implements ICellEditor {
    private hasChangedValue: boolean;
    private isEditMode: boolean;
    private readonly type: string;
    private readonly prop: IGridInputEditorProperties;
    private inner: Input;
    private view: JQuery;

    constructor(private readonly cell: GridCell, prop: IGridInputEditorProperties) {
        this.prop = $.extend({ width: '100%', height: `${defaultHeight}px` }, prop);
        this.hasChangedValue = false;
        this.isEditMode = false;
        //super(cell, initInput, $('<div>'));
    }

    public focus(): void {
        this.inner && this.inner.focus();
    }

    public beginEdit(preview = false): void {
        this.hasChangedValue = false;
        this.isEditMode = true;
        this.refreshEditor(preview);
    }

    public endEdit(preview = false): void {
        this.isEditMode = false;
        if (this.hasChangedValue && !preview) {
            this.cell.value(this.inner.getValue());
        }
        this.refreshView();
    }

    private refreshEditor(preview: boolean): void {
        this.view && this.view.detach();

        if (!this.inner) {
            this.inner = new Input(this.prop);
            this.inner.onChange(() => {
                this.hasChangedValue = true;
                if (this.prop.onEditorChanged) {
                    this.prop.onEditorChanged(this.cell, this.inner.getValue());
                }
            });
            if (this.prop.onValidate) {
                this.inner.onValidate(state => {
                    if (state) {
                        this.inner.removeError();
                    } else {
                        this.inner.setError('');
                    }
                    this.prop.onValidate(state);
                });
            }
        }

        this.inner.getControl().appendTo(this.cell.tableCell());
        if (!preview) {
            this.inner.focus();
        }

        this.inner.setValue(this.cell.value());
    }

    private refreshView(): void {
        this.inner && this.inner.getControl().detach();

        if (!this.view) {
            this.view = $('<div/>');
        }

        this.view.appendTo(this.cell.tableCell());
        this.view.text(this.prop.getText(this.cell));
    }

    private isRenderedRow(): boolean {
        return this.cell.getRow().isRendered;
    }

    public refresh(): void {
        if (this.isEditMode) {
            this.refreshEditor(true);
        } else if (this.isRenderedRow()) {
            this.refreshView();
        }
    }

    public isInActiveMode(): boolean {
        if (this.isEditMode && this.prop.type === TypeKind.dateTime) {
            return $('#ui-datepicker-div').is(':visible');
        }
        return false;
    }

    public setType(type: string): void {
        this.cell.resetEditor({ type: type });
    }

    public canTab(): boolean {
        return true;
    }

    public destroy(): void {
        safeDestroys(this.inner, this.view);
    }
}

export class GridColumnInputEditor implements IGridColumnEditor {
    public property: IGridInputEditorProperties;
    private column: GridColumn;
    private rows: RowCollection;

    constructor(prop: IGridInputEditorProperties) {
        this.property = $.extend({}, gridInputEditorDefaultProperties, prop);
    }

    public getEditor(row: GridRow, column: GridColumn, param?: any): ICellEditor {
        this.column = column;
        const prop: IGridInputEditorProperties = $.extend({}, this.property, param);
        if (prop.type == TypeKind.bool) {
            return new CellCheckbox(row.getCell(column));
        }
        if (prop.type === TypeKind.dateTime && prop.getText === gridInputEditorDefaultProperties.getText) {
            prop.getText = this.getDatetimeFormatFunc(prop);
        }
        return new GridCellInputEditor(row.getCell(column), prop);
    }

    public buildEditorForAll(column: GridColumn): void {}

    public refreshAll(rows: RowCollection): void {
        this.rows = rows;
    }

    public reInitialize(prop: IGridInputEditorProperties): void {
        this.property = $.extend({}, gridInputEditorDefaultProperties, prop);
        for (const row of this.rows.ToArray()) {
            row.getCell(this.column).resetEditor();
        }
    }

    public getDatetimeFormatFunc(prop: IGridInputEditorProperties): (cell: GridCell) => string {
        const format = localizationHelper.getInputDateTimeFormat(prop.showTime, prop['timeOnly'] || false);
        return (cell: GridCell) => {
            const value = cell.value();
            if (value instanceof Date) {
                return value.format(format);
            }
            return value || '';
        };
    }
}
